import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { useFeatureFlag } from '../utils/useFeatureFlag';
import { BRAND, Wordmark } from '../brand';
import { CalendarCheck, Calendar, Clipboard, Menu, X, Plus, LayoutDashboard, MessageSquare, BookOpen, DollarSign, LifeBuoy } from 'lucide-react';
import axios from 'axios';
import { calculateFirstMatchDate, calculateLastMatchDate, Match as LeagueDateMatch } from '../utils/leagueDates';
import FeedbackModal from './FeedbackModal';
import { IS_IOS_APP } from '../config/platform';
import { getDisplayName } from '../utils/displayName';

/**
 * Capacity/billing error modal — web only, `null` in the iOS build.
 *
 * It tells the user they need "an active subscription with available capacity"
 * and offers a mailto CTA to buy one, which is exactly the out-of-app purchase
 * link App Store guideline 3.1.1 rejects. `require` inside a branch on the raw
 * env var (rather than a top-level `import` plus a runtime check) is what lets
 * webpack fold the condition and drop the component — and its copy — out of the
 * iOS bundle entirely. See the same pattern in App.tsx.
 */
const SubscriptionErrorModal: React.ComponentType<{
  isOpen: boolean;
  onClose: () => void;
  errorMessage?: string;
}> | null =
  process.env.REACT_APP_PLATFORM === 'ios'
    ? null
    : require('./SubscriptionErrorModal').default;

const Navigation: React.FC = () => {
  const { user, logout } = useAuth();
  const { selectedTeamId, teams, setSelectedTeamId, showTeamSelector, setShowTeamSelector, refreshTeams } = useTeam();
  const location = useLocation();
  const navigate = useNavigate();
  const directoryEnabled = useFeatureFlag('directory-feature', false);
  const subscriptionsEnabled = useFeatureFlag('enable-subscriptions', false);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [modalHighlightedIndex, setModalHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const modalSearchInputRef = useRef<HTMLInputElement>(null);
  const teamButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const modalTeamButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  
  // Team creation/deletion state
  const [showAddToHomeScreenModal, setShowAddToHomeScreenModal] = useState(false);
  
  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Subscription error modal state
  const [showSubscriptionErrorModal, setShowSubscriptionErrorModal] = useState(false);
  const [subscriptionErrorMessage, setSubscriptionErrorMessage] = useState<string>('');

  // Captain status checking state
  const [captainTeamIds, setCaptainTeamIds] = useState<Set<number>>(new Set());
  const [checkingCaptainStatus, setCheckingCaptainStatus] = useState(true);

  // Active teams toggle state
  const [showActiveTeamsOnly, setShowActiveTeamsOnly] = useState(true);
  const [teamMatchesCache, setTeamMatchesCache] = useState<Map<number, LeagueDateMatch[]>>(new Map());
  
  // Invitations state
  const [userInvitations, setUserInvitations] = useState<Array<{
    id: number;
    team_id: number;
    inviter_id: number;
    status: string;
    invited_at: string;
    expires_at: string;
    team?: { id: number; name: string };
    inviter?: { id: number; name: string };
  }>>([]);
  // Note: loadingInvitations state removed - not currently used in UI

  // Detect iOS/iPadOS Safari, where "Add to Home Screen" is the only way to
  // install the PWA. Deliberately paired with !IS_IOS_APP at the call site: the
  // Capacitor WebView reports an iPhone user agent too, and telling someone
  // who already installed the native app to bookmark it is nonsense.
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Helper function to format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Helper function to get effective dates for a team
  const getEffectiveDates = (team: typeof teams[0]): { startDate: string | null; endDate: string | null } => {
    // Determine effective start date
    let effectiveStartDate: string | null = null;
    if (team.use_first_match_for_start_date) {
      const matches = teamMatchesCache.get(team.id);
      if (matches && matches.length > 0) {
        const firstMatchDate = calculateFirstMatchDate(matches);
        effectiveStartDate = firstMatchDate || team.league_start_date || null;
      } else {
        effectiveStartDate = team.league_start_date || null;
      }
    } else {
      effectiveStartDate = team.league_start_date || null;
    }

    // Determine effective end date
    let effectiveEndDate: string | null = null;
    if (team.use_last_match_for_end_date) {
      const matches = teamMatchesCache.get(team.id);
      if (matches && matches.length > 0) {
        const lastMatchDate = calculateLastMatchDate(matches);
        effectiveEndDate = lastMatchDate || team.league_end_date || null;
      } else {
        effectiveEndDate = team.league_end_date || null;
      }
    } else {
      effectiveEndDate = team.league_end_date || null;
    }

    return { startDate: effectiveStartDate, endDate: effectiveEndDate };
  };

  // Helper function to check if a team is active
  const isTeamActive = (team: typeof teams[0]): boolean => {
    if (!showActiveTeamsOnly) {
      return true; // Show all teams when toggle is off
    }

    const { startDate, endDate } = getEffectiveDates(team);

    // If either date is null/empty, consider team active
    if (!startDate || !endDate) {
      return true;
    }

    // Check if current timestamp is within the date range (inclusive)
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Set time to start of day for start date and end of day for end date
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    
    return now >= start && now <= end;
  };

  // Filter teams for modal: first by captain status, then by active status, then by search query
  const captainTeams = checkingCaptainStatus
    ? teams  // Show all teams if check is in progress
    : teams.filter(team => captainTeamIds.has(team.id));  // Only show captain teams after check completes

  // Only apply active filter after captain status check completes to prevent flicker
  const activeTeams = checkingCaptainStatus
    ? captainTeams  // Don't filter by active status while checking captain status
    : (showActiveTeamsOnly
        ? captainTeams.filter(team => isTeamActive(team))
        : captainTeams);

  const filteredModalTeams = activeTeams.filter(team => 
    team.name.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
    team.league?.toLowerCase().includes(modalSearchQuery.toLowerCase()) ||
    team.section?.toLowerCase().includes(modalSearchQuery.toLowerCase())
  );

  // Reset highlighted index when dropdown opens
  useEffect(() => {
    if (showTeamDropdown) {
      setHighlightedIndex(0);
    }
  }, [showTeamDropdown]);

  // Reset search and highlighted index when modal opens
  useEffect(() => {
    if (showTeamSelector) {
      setModalSearchQuery('');
      setModalHighlightedIndex(0);
      // Focus search input when modal opens
      setTimeout(() => modalSearchInputRef.current?.focus(), 0);
    }
  }, [showTeamSelector]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (showTeamDropdown && teamButtonRefs.current[highlightedIndex]) {
      teamButtonRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex, showTeamDropdown]);

  // Scroll highlighted item into view for modal
  useEffect(() => {
    if (showTeamSelector && modalTeamButtonRefs.current[modalHighlightedIndex]) {
      modalTeamButtonRefs.current[modalHighlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [modalHighlightedIndex, showTeamSelector]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowTeamDropdown(false);
      }
    };

    if (showTeamDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTeamDropdown]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobileMenu]);

  // Fetch user invitations
  useEffect(() => {
    const fetchInvitations = async () => {
      if (!user) {
        setUserInvitations([]);
        return;
      }

      try {
        const response = await axios.get('/invitations/invitations/me');
        setUserInvitations(response.data || []);
      } catch (error) {
        console.error('Failed to fetch invitations:', error);
        setUserInvitations([]);
      }
    };

    // Captain invitations are only ever shown inside the team-selector modal,
    // which the iOS build does not render — so don't poll for them there.
    if (user && !IS_IOS_APP) {
      fetchInvitations();
      // Refresh invitations every 30 seconds
      const interval = setInterval(fetchInvitations, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Check if user is a captain of any team
  useEffect(() => {
    const checkCaptainStatus = async () => {
      // Captain status only drives the team-selector modal, which the iOS build
      // does not render. Skipping it also drops a GET /teams/{id}/is-captain per
      // team on every mount of the nav bar.
      if (IS_IOS_APP || !user || teams.length === 0) {
        setCaptainTeamIds(new Set());
        setCheckingCaptainStatus(false);
        return;
      }

      if (user.role === 'ADMIN') {
        // Admins are captains of all teams
        setCaptainTeamIds(new Set(teams.map(t => t.id)));
        setCheckingCaptainStatus(false);
        return;
      }

      // Check if user is captain of any team
      try {
        setCheckingCaptainStatus(true);
        const captainChecks = await Promise.all(
          teams.map(async (team) => {
            try {
              const response = await axios.get(`/teams/${team.id}/is-captain`);
              const isCaptain = response.data.is_captain || response.data.is_admin;
              return { teamId: team.id, isCaptain };
            } catch (error: any) {
              // If it's a 403, user is not a captain (or not a member)
              // If it's any other error, log it but still treat as not a captain for that team
              if (error.response?.status === 403) {
                console.log(`User is not a captain of team ${team.id}`);
              } else {
                console.error(`Error checking captain status for team ${team.id}:`, error);
              }
              return { teamId: team.id, isCaptain: false };
            }
          })
        );
        
        const captainIds = new Set(
          captainChecks.filter(check => check.isCaptain).map(check => check.teamId)
        );
        setCaptainTeamIds(captainIds);
      } catch (error) {
        console.error('Failed to check captain status:', error);
        setCaptainTeamIds(new Set());
      } finally {
        setCheckingCaptainStatus(false);
      }
    };

    checkCaptainStatus();
  }, [user, teams]);

  // Fetch matches for teams when needed (when flags require match dates, regardless of toggle state)
  useEffect(() => {
    const fetchMatchesForTeams = async () => {
      if (teams.length === 0) {
        return;
      }

      // Find teams that need match data (for both active filtering and date display)
      const teamsNeedingMatches = teams.filter(team => {
        const needsStartDate = team.use_first_match_for_start_date && !team.league_start_date;
        const needsEndDate = team.use_last_match_for_end_date && !team.league_end_date;
        return (needsStartDate || needsEndDate) && !teamMatchesCache.has(team.id);
      });

      if (teamsNeedingMatches.length === 0) {
        return;
      }

      try {
        const matchPromises = teamsNeedingMatches.map(async (team) => {
          try {
            const response = await axios.get(`/matches/teams/${team.id}/matches`);
            return { teamId: team.id, matches: response.data };
          } catch (error: any) {
            // Silently handle 404s (team might not exist or user might not have access)
            // Only log non-404 errors
            if (error.response?.status !== 404) {
              console.error(`Failed to fetch matches for team ${team.id}:`, error);
            }
            return { teamId: team.id, matches: [] };
          }
        });

        const matchResults = await Promise.all(matchPromises);
        setTeamMatchesCache(prevCache => {
          const newCache = new Map(prevCache);
          matchResults.forEach(({ teamId, matches }) => {
            newCache.set(teamId, matches);
          });
          return newCache;
        });
      } catch (error) {
        console.error('Failed to fetch matches:', error);
      }
    };

    fetchMatchesForTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams]);

  // Handle keyboard navigation for modal
  const handleModalKeyDown = (e: React.KeyboardEvent) => {
    if (!showTeamSelector) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setModalHighlightedIndex(prev => 
          prev < filteredModalTeams.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setModalHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredModalTeams[modalHighlightedIndex]) {
          handleModalTeamSelect(filteredModalTeams[modalHighlightedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowTeamSelector(false);
        break;
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const handleModalTeamSelect = (teamId: number) => {
    setSelectedTeamId(teamId);
    setShowTeamSelector(false);
    setModalSearchQuery('');
  };

  // Handle team creation - navigate to wizard
  const handleCreateTeam = () => {
    navigate('/teams/create-wizard');
  };


  // Admin is a web-only surface; the iOS build ships player features only.
  const showAdminLink = !IS_IOS_APP && user?.role === 'ADMIN';

  return (
    <>
      {/* Combined Navigation Bar */}
      <nav 
        className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl border-b border-white/30 dark:border-gray-700/30 shadow-lg sticky top-0 z-40 w-full"
        style={{
          // Extend background to edges on mobile
          marginLeft: 'calc(-1 * env(safe-area-inset-left, 0px))',
          marginRight: 'calc(-1 * env(safe-area-inset-right, 0px))',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          width: 'calc(100% + env(safe-area-inset-left, 0px) + env(safe-area-inset-right, 0px))',
        }}
      >
        <div 
          className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8"
          style={{
            paddingTop: 'env(safe-area-inset-top, 0px)',
          }}
        >
          {/* Top Row: Brand, Team Selector, User Profile */}
          <div className="flex justify-between items-center h-16">
            {/* Left side - Hamburger (mobile/tablet) and Brand */}
            <div className="flex items-center space-x-3 lg:space-x-6 flex-1 lg:flex-initial min-w-0">
              {/* Hamburger Menu Button - Mobile/Tablet Only */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                aria-label="Toggle menu"
              >
                {showMobileMenu ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>

              <Link to="/dashboard" className="flex items-center space-x-2 text-base sm:text-lg lg:text-xl font-semibold text-gray-900 dark:text-gray-100 hover:text-primary-600 min-w-0 flex-shrink">
                <img src={BRAND.logoSrc} alt={BRAND.name} className="h-7 w-7 lg:h-8 lg:w-8 flex-shrink-0" />
                <span className="hidden sm:inline truncate"><Wordmark /></span>
                <span className="sm:hidden">{BRAND.useWordmark ? <Wordmark /> : BRAND.shortName}</span>
              </Link>
            </div>

            {/* Center - Navigation Links - Desktop Only */}
            <div className="hidden lg:flex flex-1 justify-start items-center">
              <div className="flex space-x-8 items-center pl-6">
                <Link
                  to="/dashboard"
                  onClick={() => setShowMobileMenu(false)}
                  className={`text-sm font-medium whitespace-nowrap border-b-2 h-12 flex items-center transition-colors min-h-[44px] px-2 ${
                    isActive('/dashboard')
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Link>
                <Link
                  to="/availability"
                  onClick={() => setShowMobileMenu(false)}
                  className={`text-sm font-medium whitespace-nowrap border-b-2 h-12 flex items-center transition-colors min-h-[44px] px-2 ${
                    isActive('/availability')
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <CalendarCheck className="w-4 h-4 mr-2" />
                  My Availability
                </Link>
                <Link
                  to="/matches"
                  onClick={() => setShowMobileMenu(false)}
                  className={`text-sm font-medium whitespace-nowrap border-b-2 h-12 flex items-center transition-colors min-h-[44px] px-2 ${
                    isActive('/matches')
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  My Matches
                </Link>
                {/* Captain surface — deferred to v2, absent from the iOS build. */}
                {!IS_IOS_APP && (
                  <Link
                    to="/teams"
                    onClick={() => setShowMobileMenu(false)}
                    className={`text-sm font-medium whitespace-nowrap border-b-2 h-12 flex items-center transition-colors min-h-[44px] px-2 ${
                      isActive('/teams')
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <Clipboard className="w-4 h-4 mr-2" />
                    Manage Teams
                  </Link>
                )}
              </div>
            </div>

            {/* Right side - User Profile */}
            <div className="flex items-center space-x-2 lg:space-x-6 flex-shrink-0">
              <div className="flex items-center space-x-2 lg:space-x-3">
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 lg:space-x-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg px-2 lg:px-3 py-2 transition-colors min-h-[44px] min-w-[44px] lg:min-w-auto"
                  >
                    <div className="text-right hidden xl:block">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{user ? getDisplayName(user) : ''}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</div>
                    </div>
                    {user?.picture_url ? (
                      <img 
                        src={user.picture_url} 
                        alt={getDisplayName(user)}
                        className="w-8 h-8 rounded-full border-2 border-gray-200 dark:border-gray-700 flex-shrink-0"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium flex-shrink-0">
                        {user ? getDisplayName(user).charAt(0).toUpperCase() : ''}
                      </div>
                    )}
                    <svg
                      className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform flex-shrink-0 ${showUserMenu ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* User Menu Dropdown */}
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-xl border border-white/30 dark:border-gray-700/30 py-1 z-50">
                      <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                        {user?.role}
                      </div>
                      {showAdminLink && (
                        <Link
                          to="/admin"
                          onClick={() => setShowUserMenu(false)}
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-gray-700 hover:text-primary-600 transition-colors min-h-[44px] flex items-center"
                        >
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>Admin Panel</span>
                          </div>
                        </Link>
                      )}
                      <Link
                        to="/profile"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors min-h-[44px] flex items-center"
                      >
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>My Profile</span>
                        </div>
                      </Link>
                      {/* No billing surface in the iOS build — App Store 3.1.1.
                          !IS_IOS_APP leads deliberately: as the first operand it
                          is a literal false there, so terser folds the entry —
                          and its '/subscription' href — out of the bundle
                          instead of just leaving it unrendered. */}
                      {!IS_IOS_APP && subscriptionsEnabled && (
                        <Link
                          to="/subscription"
                          onClick={() => setShowUserMenu(false)}
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors min-h-[44px] flex items-center"
                        >
                          <div className="flex items-center space-x-2">
                            <DollarSign className="w-4 h-4" />
                            <span>Subscription</span>
                          </div>
                        </Link>
                      )}
                      {directoryEnabled && (
                        <Link
                          to="/directory"
                          onClick={() => setShowUserMenu(false)}
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors min-h-[44px] flex items-center"
                        >
                          <div className="flex items-center space-x-2">
                            <BookOpen className="w-4 h-4" />
                            <span>Directory</span>
                          </div>
                        </Link>
                      )}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          setShowFeedbackModal(true);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors min-h-[44px] flex items-center"
                      >
                        <div className="flex items-center space-x-2">
                          <MessageSquare className="w-4 h-4" />
                          <span>Share Feedback</span>
                        </div>
                      </button>
                      <a
                        href="mailto:support@6love.app"
                        onClick={() => setShowUserMenu(false)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors min-h-[44px] flex items-center"
                      >
                        <div className="flex items-center space-x-2">
                          <LifeBuoy className="w-4 h-4" />
                          <span>Contact Support</span>
                        </div>
                      </a>
                      {!IS_IOS_APP && isIOS() && (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowAddToHomeScreenModal(true);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors min-h-[44px] flex items-center"
                        >
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <span>Add to Home Screen</span>
                          </div>
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          logout();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 transition-colors min-h-[44px] flex items-center"
                      >
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span>Logout</span>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setShowMobileMenu(false)}
          />
          <div className={`fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-white dark:bg-gray-900 shadow-xl z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${
            showMobileMenu ? 'translate-x-0' : '-translate-x-full'
          }`}>
            <div className="flex flex-col h-full">
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Menu</h2>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Mobile Menu Content */}
              <div className="flex-1 overflow-y-auto">
                {/* Navigation Links - Mobile */}
                <div className="py-2">
                  <Link
                    to="/dashboard"
                    onClick={() => setShowMobileMenu(false)}
                    className={`flex items-center px-4 py-3 text-base font-medium transition-colors min-h-[44px] ${
                      isActive('/dashboard')
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-l-4 border-primary-600 dark:border-primary-400'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <LayoutDashboard className="w-5 h-5 mr-3" />
                    Dashboard
                  </Link>
                  <Link
                    to="/availability"
                    onClick={() => setShowMobileMenu(false)}
                    className={`flex items-center px-4 py-3 text-base font-medium transition-colors min-h-[44px] ${
                      isActive('/availability')
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-l-4 border-primary-600 dark:border-primary-400'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <CalendarCheck className="w-5 h-5 mr-3" />
                    My Availability
                  </Link>
                  <Link
                    to="/matches"
                    onClick={() => setShowMobileMenu(false)}
                    className={`flex items-center px-4 py-3 text-base font-medium transition-colors min-h-[44px] ${
                      isActive('/matches')
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-l-4 border-primary-600 dark:border-primary-400'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Calendar className="w-5 h-5 mr-3" />
                    My Matches
                  </Link>
                  {/* Captain surface — deferred to v2, absent from the iOS build. */}
                  {!IS_IOS_APP && (
                    <Link
                      to="/teams"
                      onClick={() => setShowMobileMenu(false)}
                      className={`flex items-center px-4 py-3 text-base font-medium transition-colors min-h-[44px] ${
                        isActive('/teams')
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-l-4 border-primary-600 dark:border-primary-400'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <Clipboard className="w-5 h-5 mr-3" />
                      Manage Teams
                    </Link>
                  )}
                </div>

                {/* User Menu Items - Mobile */}
                {showAdminLink && (
                  <div className="border-t border-gray-200 dark:border-gray-700 py-2">
                    <Link
                      to="/admin"
                      onClick={() => setShowMobileMenu(false)}
                      className="flex items-center px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors min-h-[44px]"
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Admin Panel
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Team Selection Modal — captain-only, so web only. */}
      {!IS_IOS_APP && showTeamSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Select a Team</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {captainTeams.length > 0 ? "Please select which team you'd like to work with." : "You are not a captain of any teams yet."}
                </p>
              </div>
              <button
                onClick={() => setShowTeamSelector(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0 ml-2"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
              {/* Pending Invitations Section */}
              {userInvitations.filter(inv => inv.status === 'pending').length > 0 && (
                <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Pending Captain Invitations
                  </h3>
                  <div className="space-y-2">
                    {userInvitations
                      .filter(inv => inv.status === 'pending')
                      .map((invitation) => {
                        const team = teams.find(t => t.id === invitation.team_id);
                        return (
                          <div
                            key={invitation.id}
                            className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {team?.name || `Team #${invitation.team_id}`}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Invitation sent {new Date(invitation.invited_at).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-3">
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.post(`/invitations/invitations/${invitation.id}/accept`);
                                    // Refresh teams and invitations
                                    const [invitationsResponse] = await Promise.all([
                                      axios.get('/invitations/invitations/me')
                                    ]);
                                    setUserInvitations(invitationsResponse.data || []);
                                    // Refresh teams and select the accepted team
                                    await refreshTeams();
                                    setSelectedTeamId(invitation.team_id);
                                    setShowTeamSelector(false);
                                  } catch (error: any) {
                                    console.error('Failed to accept invitation:', error);
                                    const errorMessage = error.response?.data?.detail || 'Failed to accept invitation';
                                    setSubscriptionErrorMessage(errorMessage);
                                    setShowSubscriptionErrorModal(true);
                                  }
                                }}
                                className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors min-h-[32px]"
                              >
                                Accept
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    await axios.post(`/invitations/invitations/${invitation.id}/decline`);
                                    const response = await axios.get('/invitations/invitations/me');
                                    setUserInvitations(response.data || []);
                                  } catch (error: any) {
                                    console.error('Failed to decline invitation:', error);
                                    alert(error.response?.data?.detail || 'Failed to decline invitation');
                                  }
                                }}
                                className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors min-h-[32px]"
                              >
                                Decline
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {captainTeams.length > 0 ? (
                <>
                  {/* Active Teams Toggle */}
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Show active teams only
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={showActiveTeamsOnly}
                      onClick={() => setShowActiveTeamsOnly(!showActiveTeamsOnly)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        showActiveTeamsOnly ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          showActiveTeamsOnly ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Search Input */}
                  <div className="mb-4">
                    <input
                      ref={modalSearchInputRef}
                      type="text"
                      value={modalSearchQuery}
                      onChange={(e) => {
                        setModalSearchQuery(e.target.value);
                        setModalHighlightedIndex(0);
                      }}
                      placeholder="Search teams..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[44px]"
                      onKeyDown={handleModalKeyDown}
                    />
                  </div>
                  
                  {/* Teams List */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {filteredModalTeams.length > 0 ? (
                      filteredModalTeams.map((team, index) => {
                        const { startDate, endDate } = getEffectiveDates(team);
                        
                        return (
                          <button
                            key={team.id}
                            ref={el => { modalTeamButtonRefs.current[index] = el; }}
                            onClick={() => handleModalTeamSelect(team.id)}
                            onMouseEnter={() => setModalHighlightedIndex(index)}
                            className={`w-full text-left px-4 py-3 border rounded-lg transition-colors flex items-center justify-between min-h-[44px] ${
                              team.id === selectedTeamId
                                ? 'bg-primary-600 dark:bg-primary-700 border-primary-600 dark:border-primary-600 text-white'
                                : index === modalHighlightedIndex
                                  ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-300 dark:border-primary-600'
                                  : 'border-gray-200 dark:border-gray-600 hover:bg-primary-50 dark:hover:bg-gray-700 hover:border-primary-300 dark:hover:border-primary-500'
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium whitespace-nowrap ${team.id === selectedTeamId ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                                {team.name}
                              </div>
                              {(startDate || endDate) && (
                                <div className={`text-xs mt-1 ${team.id === selectedTeamId ? 'text-primary-100' : 'text-gray-500 dark:text-gray-400'}`}>
                                  {startDate && endDate
                                    ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                                    : startDate
                                    ? `Starts ${formatDate(startDate)}`
                                    : `Ends ${formatDate(endDate)}`}
                                </div>
                              )}
                            </div>
                            {team.id === selectedTeamId && (
                              <svg className="w-5 h-5 text-white flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center whitespace-nowrap">
                        No teams found
                      </div>
                    )}
                  </div>

                  {/* Create New Team Button */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        setShowTeamSelector(false);
                        handleCreateTeam();
                      }}
                      className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm flex items-center justify-center space-x-2 min-h-[44px]"
                    >
                      <Plus className="w-5 h-5" />
                      <span>Create New Team</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="px-4 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
                  <div className="mb-4">
                    <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-gray-600 dark:text-gray-300">You are not a captain of any teams yet.</p>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">Contact your team administrator to be added as a captain, or create a new team.</p>
                  </div>
                  {user && (
                    <button
                      onClick={() => {
                        handleCreateTeam();
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm flex items-center justify-center space-x-2 mx-auto min-h-[44px]"
                    >
                      <Plus className="w-5 h-5" />
                      <span>Create New Team</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}



      {/* Add to Home Screen Modal — PWA install hint, meaningless once native. */}
      {!IS_IOS_APP && showAddToHomeScreenModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-lg shadow-2xl border border-white/30 dark:border-gray-700/30 p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Add to Home Screen</h2>
              <button
                onClick={() => setShowAddToHomeScreenModal(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Add {BRAND.name} to your home screen for quick access:
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-sm">
                    1
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">Tap the Share button</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Look for the share icon <svg className="w-4 h-4 inline-block mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg> at the bottom of your screen
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-sm">
                    2
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">Select "Add to Home Screen"</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Scroll down in the share menu and tap "Add to Home Screen"
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-sm">
                    3
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">Confirm and enjoy!</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Tap "Add" to confirm. The app icon will appear on your home screen.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAddToHomeScreenModal(false)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />

      {/* Subscription Error Modal */}
      {SubscriptionErrorModal && (
        <SubscriptionErrorModal
          isOpen={showSubscriptionErrorModal}
          onClose={() => setShowSubscriptionErrorModal(false)}
          errorMessage={subscriptionErrorMessage}
        />
      )}
    </>
  );
};

export default Navigation;
